/**
 * Open Jobs Aging Report (20.7) — color-coded rows, bottleneck flags, status chart.
 */

import { useCallback, useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from 'recharts';
import { fetchOpenJobsAgingReport, exportOpenJobsAgingCSV, exportOpenJobsAgingPDF } from '../../api/reports';
import { LoadingSpinner, EmptyState } from '../common';
import ReportShell from './ReportShell';

const STATUS_COLORS = {
  'Quote': '#94a3b8',
  'Pending Review': '#a78bfa',
  'Approved': '#60a5fa',
  'BOM Prep': '#38bdf8',
  'Quoting Parts': '#2dd4bf',
  'Parts Ordered': '#4ade80',
  'Parts Partial': '#a3e635',
  'Parts Ready': '#facc15',
  'Build Paused': '#f97316',
  'Build Ready': '#fb923c',
  'In Build': '#f472b6',
};

function rowColor(row) {
  if (row.bottleneck === 'critical') return 'bg-red-50';
  if (row.bottleneck === 'warning') return 'bg-yellow-50';
  if (row.is_overdue) return 'bg-red-50';
  return '';
}

export default function OpenJobsAgingReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [exporting, setExporting] = useState(null);

  const params = {
    status_filter: statusFilter || undefined,
    customer_id: customerFilter || undefined,
    priority: priorityFilter || undefined,
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await fetchOpenJobsAgingReport(params)); }
    catch { setData(null); }
    finally { setLoading(false); }
  }, [statusFilter, customerFilter, priorityFilter]);

  useEffect(() => { load(); }, [load]);

  async function handleExport(type) {
    setExporting(type);
    try {
      if (type === 'csv') await exportOpenJobsAgingCSV(params);
      else await exportOpenJobsAgingPDF(params);
    } catch {} finally { setExporting(null); }
  }

  const rows = data?.rows || [];
  const summary = data?.summary || {};

  // Build chart data from status breakdown
  const chartData = Object.entries(summary.status_breakdown || {}).map(([status, count]) => ({
    status,
    count,
    fill: STATUS_COLORS[status] || '#6b7280',
  }));

  return (
    <ReportShell
      title="Open Jobs Aging"
      exporting={exporting}
      onExportCSV={() => handleExport('csv')}
      onExportPDF={() => handleExport('pdf')}
      filters={
        <>
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Status</label>
            <select
              className="border border-gray-300 rounded-md px-2 py-1 text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="quote">Quote</option>
              <option value="pending_review">Pending Review</option>
              <option value="approved">Approved</option>
              <option value="bom_prep">BOM Prep</option>
              <option value="quoting_parts">Quoting Parts</option>
              <option value="parts_ordered">Parts Ordered</option>
              <option value="parts_partial">Parts Partial</option>
              <option value="parts_ready">Parts Ready</option>
              <option value="build_paused">Build Paused</option>
              <option value="build_ready">Build Ready</option>
              <option value="in_build">In Build</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Priority</label>
            <select
              className="border border-gray-300 rounded-md px-2 py-1 text-sm"
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Customer ID</label>
            <input
              type="text"
              className="border border-gray-300 rounded-md px-2 py-1 text-sm w-40"
              placeholder="Filter..."
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
            />
          </div>
          <button onClick={load} className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 self-end">Run</button>
        </>
      }
    >
      {loading ? <div className="flex justify-center py-10"><LoadingSpinner /></div> : rows.length === 0 ? (
        <EmptyState title="No open jobs" description="All jobs are complete or archived." />
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
              <div className="text-2xl font-bold text-foreground">{summary.total_open_jobs}</div>
              <div className="text-xs text-gray-500">Open Jobs</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
              <div className="text-2xl font-bold text-red-600">{summary.overdue_count}</div>
              <div className="text-xs text-gray-500">Overdue</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
              <div className="text-2xl font-bold text-red-500">{summary.bottleneck_critical}</div>
              <div className="text-xs text-gray-500">Critical (&gt;14d)</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
              <div className="text-2xl font-bold text-yellow-600">{summary.bottleneck_warning}</div>
              <div className="text-xs text-gray-500">Warning (&gt;7d)</div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                  <th className="px-3 py-2">Job #</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Priority</th>
                  <th className="px-3 py-2">Customer</th>
                  <th className="px-3 py-2">Item</th>
                  <th className="px-3 py-2 text-right">Age (days)</th>
                  <th className="px-3 py-2 text-right">In Status</th>
                  <th className="px-3 py-2">Due</th>
                  <th className="px-3 py-2 text-right">Parts %</th>
                  <th className="px-3 py-2 text-right">Labor %</th>
                  <th className="px-3 py-2">Flag</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r) => (
                  <tr key={r.job_id} className={`${rowColor(r)} hover:bg-gray-50`}>
                    <td className="px-3 py-2 font-medium">{r.job_number}</td>
                    <td className="px-3 py-2 text-gray-500 capitalize">{r.job_type}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                        {r.status_display}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={
                        r.priority === 'urgent' ? 'text-red-600 font-semibold' :
                        r.priority === 'high' ? 'text-orange-600 font-medium' :
                        'text-gray-500 capitalize'
                      }>
                        {r.priority}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-600">{r.customer_name}</td>
                    <td className="px-3 py-2 text-gray-500 text-xs">{r.parent_item_no}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.total_age_days}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.days_in_status}</td>
                    <td className="px-3 py-2 text-xs text-gray-400">
                      {r.target_due_date || '—'}
                      {r.is_overdue && <span className="ml-1 text-red-600 font-medium">LATE</span>}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-500">{r.percent_parts_by_lines}%</td>
                    <td className="px-3 py-2 text-right text-gray-500">{r.percent_labor_complete}%</td>
                    <td className="px-3 py-2">
                      {r.bottleneck === 'critical' && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700">CRITICAL</span>
                      )}
                      {r.bottleneck === 'warning' && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-yellow-100 text-yellow-700">WARNING</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {chartData.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-medium text-foreground mb-3">Jobs by Status</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="status" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" name="Jobs">
                    {chartData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </ReportShell>
  );
}
